/**
 * CORE-W-02 lead detail drawer.
 *
 * Chans's 2026-09-15 rebuild spec §CORE-W-02: "Embed the WhatConverts
 * lead UI natively in the page — the same way Agency Analytics was
 * brought into the dashboard. Interactive inside Core, same look and
 * feel: the full lead list, editable lead values, lead scoring."
 *
 * The reconciliation doc §CORE-W-02 to W-04 adds: "filter bar, lead
 * drawer with trace back to search term, editable value and stage."
 *
 * What ships here
 *
 *   - The drawer skeleton: opens from a row click, shows the lead's
 *     full metadata (contact, source/medium, gclid, quote value,
 *     stage, date) and the trace-back panel (search term where
 *     available; a plain-English note when the lead did not carry a
 *     search term).
 *   - The "quote value" and "stage" fields render as VIEW-ONLY today.
 *     Chans's spec calls for editable, but the backend write endpoint
 *     is not there yet (see whatconverts.py — only GET routes today).
 *     The editable UI lands with CORE-W-02.b once the backend can
 *     accept the write.
 *   - A single "coming with W-02.b" note sits below the view-only
 *     fields so an operator opening the drawer today sees what will
 *     become editable and knows why they can't edit right now.
 *
 * What is deliberately not here
 *
 *   - No inline edit form. Building the form before the write endpoint
 *     is a "written but never wired" pattern — the field would look
 *     editable, do nothing on Save, and erode trust.
 *   - No optimistic update. The queryKey shape is not settled until
 *     the write endpoint exists.
 */
import type { WcLead, WcQuotable } from '@/services/whatconvertsService';
import { adsRelativeAge } from '@/lib/adsMetricFormat';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Info } from 'lucide-react';

import { LeadLabelsPanel } from './LeadLabelsPanel';
import { quotableExplanation, wcFlagReason } from './quotableExplanation';

const QUOTABLE_LABEL: Record<WcQuotable, string> = {
  yes: 'Quotable',
  no: 'Not quotable',
  pending: 'Pending',
  not_set: 'Not set',
};

const LEAD_TYPE_LABEL: Record<string, string> = {
  phone_call: 'Phone call',
  web_form: 'Web form',
  chat: 'Chat',
  text_message: 'Text',
  email: 'Email',
};

function usd(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${v.toLocaleString()}`;
}

/**
 * CORE-W-02.e: leads arrived stamped `2026-09-22T00:20:28Z` in both the
 * drawer header and the Date row. An operator reading a lead wants the
 * moment it came in, not an ISO string, and every other date in the tool
 * is formatted. Exact date + time here (not `adsRelativeAge`) because
 * "when exactly did this call come in" is the question the drawer answers;
 * the header carries the relative age for scanning.
 */
function leadTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso);
  // An unparseable stamp must not render "Invalid Date".
  if (Number.isNaN(t.getTime())) return iso;
  return t.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The trace-back a Chans-spec §W-02 lead drawer must show. Not every
 * lead carries a search term (organic form fills, direct calls); when it
 * is absent the drawer says so in plain English rather than rendering
 * an empty row.
 */
function TraceBackPanel({ lead }: { lead: WcLead }) {
  const hasGclid = Boolean(lead.gclid);
  return (
    <div className="rounded-md border border-border bg-warm-bg p-3 text-sm">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Trace back to the click
      </div>
      {hasGclid ? (
        <>
          <div className="space-y-1.5">
            <FieldRow label="Click ID" value={lead.gclid ?? '—'} mono />
            <FieldRow
              label="Source"
              value={`${lead.lead_source ?? '—'}${
                lead.lead_medium && lead.lead_medium !== 'none'
                  ? ` / ${lead.lead_medium}`
                  : ''
              }`}
            />
          </div>
          {/* CORE-W-02.c: this used to read "The offline-conversion
              upload pairs it with the search term that drove the click."
              That path is retired — the module is already deleted on
              staging and main — so the sentence described machinery an
              operator's account does not have. The copy now says what the
              click ID IS and claims no pipeline, which keeps it true on
              every branch. */}
          <p className="mt-2 text-xs text-muted-foreground">
            This lead carries a Google click ID. That is the key a
            conversion has to carry for Google Ads to credit it to the
            search term behind the click.
          </p>
        </>
      ) : (
        <p className="text-muted-foreground">
          No Google click ID recorded. The lead came in from{' '}
          <span className="font-medium text-foreground">
            {lead.lead_source ?? 'an unknown source'}
          </span>
          {lead.lead_medium && lead.lead_medium !== 'none'
            ? ` / ${lead.lead_medium}`
            : ''}
          . Without a click ID, Google Ads cannot attribute this lead to a
          specific search term.
        </p>
      )}
    </div>
  );
}

/** Under the Stage row: what a lead that is not quotable means here. */
function QuotableNote({ lead }: { lead: WcLead }) {
  const text = quotableExplanation(lead);
  if (!text) return null;
  const reason = wcFlagReason(lead);
  return (
    <p className="text-xs text-muted-foreground" data-testid="quotable-explanation">
      {reason ? <span className="font-medium text-foreground">{reason} </span> : null}
      {text}
    </p>
  );
}

function FieldRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-xs text-teal-dark' : 'font-medium'}>
        {value}
      </span>
    </div>
  );
}

export interface LeadDrawerProps {
  lead: WcLead | null;
  onClose: () => void;
  /** The tenant the page reads; needed for the quotable check block. */
  tenantId?: string | null;
  /** Lead labels are on and readable: show the quotable check block. */
  labelsOn?: boolean;
}

export function LeadDrawer({ lead, onClose, tenantId, labelsOn }: LeadDrawerProps) {
  return (
    <Sheet open={lead !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full max-w-md sm:max-w-lg">
        {lead ? (
          <>
            <SheetHeader>
              <SheetTitle>
                {lead.contact_name || LEAD_TYPE_LABEL[lead.lead_type] || 'Lead'}
              </SheetTitle>
              <SheetDescription>
                #{lead.lead_id} · {LEAD_TYPE_LABEL[lead.lead_type] ?? lead.lead_type}
                {lead.date_created
                  ? ` · ${adsRelativeAge(lead.date_created) ?? leadTimestamp(lead.date_created)}`
                  : ''}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-4">
              <div className="rounded-md border border-border bg-warm-bg p-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Lead
                </div>
                <div className="space-y-1.5">
                  <FieldRow
                    label="Contact"
                    value={lead.contact_name || '—'}
                  />
                  <FieldRow
                    label="Type"
                    value={LEAD_TYPE_LABEL[lead.lead_type] ?? lead.lead_type}
                  />
                  <FieldRow label="Date" value={leadTimestamp(lead.date_created)} />
                </div>
              </div>

              {labelsOn && tenantId ? <LeadLabelsPanel lead={lead} tenantId={tenantId} /> : null}

              <TraceBackPanel lead={lead} />

              <div className="rounded-md border border-border bg-warm-bg p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Quote value + stage
                  </div>
                  <Badge
                    variant="outline"
                    aria-label="Editing lead value and stage is not available yet"
                    className="text-[10px]"
                  >
                    Editing coming next
                  </Badge>
                </div>
                <div className="space-y-1.5">
                  <FieldRow
                    label="Value"
                    value={usd(lead.quote_value)}
                  />
                  <FieldRow
                    label="Stage"
                    value={QUOTABLE_LABEL[lead.quotable] ?? lead.quotable}
                  />
                  <QuotableNote lead={lead} />
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
                  {/* CORE-W-02.d: "CORE-W-02.b" was printed here. A
                      ticket ID means nothing to an operator and has to be
                      explained; the plain sentence reads better anyway. */}
                  Editing lead value and stage from Core is not available
                  yet, because the WhatConverts write endpoint is not
                  wired. For now, edit these in WhatConverts and Core
                  picks them up on the next read.
                </p>
              </div>
            </div>

            <SheetFooter className="mt-6">
              {/* This codebase's SheetClose (DialogClose) takes no asChild, so
                  wrapping the Button nested a button in a button. The drawer's
                  own onClose closes the sheet the same way. */}
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </SheetFooter>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

export default LeadDrawer;
